import { parsePhoneNumber } from "awesome-phonenumber";
import { z } from "zod";

/** True when `value` parses as a valid number (US assumed unless it starts with `+`). */
export function isValidPhone(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return parsePhoneNumber(trimmed, { regionCode: "US" }).valid;
}

/**
 * E.164 for API payloads and identity comparison. Returns the input unchanged
 * when it does not parse — callers that need a hard guarantee validate first.
 */
export function toPhoneE164(value: string): string {
  const pn = parsePhoneNumber(value.trim(), { regionCode: "US" });
  return pn.valid ? (pn.number?.e164 ?? value) : value;
}

/**
 * THE phone-field schema. Validate-only: the stored value is exactly what the
 * user typed (already national-formatted by `PhoneInput`). Convert with
 * `toPhoneE164` at the API boundary — never inside the schema.
 */
export function createPhoneSchema(options?: {
  requiredMessage?: string;
  invalidMessage?: string;
}) {
  const {
    requiredMessage = "Please enter your phone number.",
    invalidMessage = "Please enter a valid phone number.",
  } = options ?? {};
  return z
    .string()
    .trim()
    .min(1, requiredMessage)
    .refine(isValidPhone, invalidMessage);
}

export const phoneSchema = createPhoneSchema();

/** Same rules, but an empty string is allowed (an optional phone input). */
export const optionalPhoneSchema = z.union([z.literal(""), phoneSchema]);
